import React, { useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Mesh, Spherical, MathUtils, RepeatWrapping } from "three";
import { useTexture } from "@react-three/drei";
import { ceresTextureSrc, ceresDisplacementSrc, sections, Section } from "./constants.ts";

type MoonProps = {
  setActiveNav: React.Dispatch<React.SetStateAction<Section>>;
  setCoords: React.Dispatch<React.SetStateAction<string>>;
};

export function Sphere({ setActiveNav, setCoords }: MoonProps) {
  const meshRef = useRef<Mesh>(null);
  const { camera } = useThree();
  const prevCoordsRef = useRef<string>("");
  const prevSectionRef = useRef<Section | null>(null);

  const [texture, displacementMap] = useTexture([
    ceresTextureSrc,
    ceresDisplacementSrc,
  ]);

  texture.wrapS = texture.wrapT = RepeatWrapping;
  displacementMap.wrapS = displacementMap.wrapT = RepeatWrapping;

  const [angleOffset, setAngleOffset] = useState<{ theta: number; phi: number } | null>(null);

  useFrame(() => {
    // Rotate the mesh slowly
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.00025;
    }

    const spherical = new Spherical().setFromVector3(camera.position.clone());

    // If angleOffset is not set, calculate it based on a target location
    if (!angleOffset) {
      const targetLat = 1.2874;
      const targetLon = 103.84899;
      const targetTheta = MathUtils.degToRad(targetLon);
      const targetPhi = MathUtils.degToRad(90 - targetLat);
      setAngleOffset({
        theta: spherical.theta - targetTheta,
        phi: spherical.phi - targetPhi,
      });
      return;
    }

    const correctedTheta = spherical.theta - angleOffset.theta;
    const correctedPhi = spherical.phi - angleOffset.phi;

    // Ensure correctedPhi is within valid range
    let rawLon = MathUtils.radToDeg(correctedTheta);
    if (rawLon > 180) rawLon -= 360;
    const rawLat = 90 - MathUtils.radToDeg(correctedPhi);

    // Format coordinates
    const lat = (Math.abs(rawLat) > 89.999 ? 90 : Math.abs(rawLat)).toFixed(4);
    const lon = Math.abs(rawLon).toFixed(4);
    const latDir = rawLat >= 0 ? "N" : "S";
    const lonDir = rawLon >= 0 ? "E" : "W";
    const formatted = `${lat}° ${latDir}, ${lon}° ${lonDir}`;

    if (formatted !== prevCoordsRef.current) {
      setCoords(formatted);
      prevCoordsRef.current = formatted;
    }

    // Determine the active section based on theta
    let thetaNormalized = spherical.theta % (2 * Math.PI);
    if (thetaNormalized < 0) thetaNormalized += 2 * Math.PI;
    const segmentAngle = (2 * Math.PI) / sections.length;
    const index = Math.floor((thetaNormalized + segmentAngle / 2) / segmentAngle) % sections.length;
    const newActive = sections[index];

    if (prevSectionRef.current !== newActive) {
      prevSectionRef.current = newActive;
      setActiveNav(newActive);
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[3, 64, 64]} />
      <meshStandardMaterial
        map={texture}
        displacementMap={displacementMap}
        displacementScale={0}
        bumpMap={displacementMap}
        bumpScale={1}
      />
    </mesh>
  );
}
